import Phaser from "phaser";
import { ensureRealtime } from "@/network/realtime";
import { useCharacterStore } from "@/state/characterStore";
import { useUIStore } from "@/state/uiStore";
import itemsData from "@/data/items.json";
import { inventory } from "@/systems/InventorySystem";
import { usePotionShortcutStore } from "@/state/potionShortcutStore";
import { getSettingsSnapshot } from "@/state/settingsStore";
import type { PickupResp, EquipPickupResp } from "@/network/messages";
import type { Monster } from "@/entities/Monster";
import type { ZenPile } from "@/entities/ZenPile";
import type { EquipPile } from "@/entities/EquipPile";
import type { FieldScene } from "../FieldScene";
import {
  FIELD_PIX_W,
  FIELD_PIX_H,
  MOVE,
  PICKUP,
  PICKUPEQUIP,
  USEITEM,
} from "./FieldConstants";

export function createFieldAutomation(scene: FieldScene) {
  function findNearestMonster(maxRange = 600): Monster | undefined {
    if (!scene.localPlayer) return undefined;
    const px = scene.localPlayer.x;
    const py = scene.localPlayer.y;
    let best: Monster | undefined;
    let bestD = maxRange * maxRange;
    for (const m of scene.monsters.values()) {
      if (!m.alive) continue;
      const dx = m.x - px;
      const dy = m.y - py;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = m;
      }
    }
    return best;
  }

  // While in auto-attack, walk the local player toward the nearest pickable Zen pile
  // when it's outside auto-pickup range. Only one pile per tick; respects owner-window.
  function autoWalkToNearestPile(delta: number, time: number) {
    if (!scene.localPlayer || scene.zenPiles.size === 0) return;
    const cfg = getSettingsSnapshot();
    if (!cfg.autoWalkToLoot || !cfg.autoPickupZen) return;
    const px = scene.localPlayer.x;
    const py = scene.localPlayer.y;
    const now = Date.now();
    const maxScan = 480;
    let best: ZenPile | undefined;
    let bestD = maxScan * maxScan;
    for (const pile of scene.zenPiles.values()) {
      if (!pile.alive) continue;
      if (
        pile.ownerUid &&
        pile.ownerUid !== scene.localUid &&
        (!pile.ownerUntil || now < pile.ownerUntil)
      )
        continue;
      const dx = pile.x - px;
      const dy = pile.y - py;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = pile;
      }
    }
    if (!best) return;
    if (bestD <= scene.PICKUP_RANGE * scene.PICKUP_RANGE) return;

    const dx = best.x - px;
    const dy = best.y - py;
    const dist = Math.hypot(dx, dy) || 1;
    const speed = 0.2 * delta;
    let nx = Phaser.Math.Clamp(
      px + (dx / dist) * speed,
      16,
      FIELD_PIX_W - 16,
    );
    let ny = Phaser.Math.Clamp(
      py + (dy / dist) * speed,
      16,
      FIELD_PIX_H - 16,
    );
    const c = scene.collide(px, py, nx, ny);
    nx = c.x;
    ny = c.y;
    if (nx === px && ny === py) return;
    scene.localPlayer.x = nx;
    scene.localPlayer.y = ny;
    const dir =
      Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 2 : 0;
    useCharacterStore.getState().setPosition({ x: nx, y: ny });
    if (time - scene.lastSent > 100) {
      scene.lastSent = time;
      ensureRealtime()
        .then((cli) => cli.notify(MOVE, { x: nx, y: ny, d: dir }))
        .catch(() => {
          /* swallow */
        });
    }
  }

  function toggleAutoAttack() {
    scene.autoAttack = !scene.autoAttack;
    if (scene.autoAttack) {
      if (!scene.targetMonster || !scene.targetMonster.alive) {
        const next = findNearestMonster();
        if (next) {
          scene.targetMonster = next;
          next.setSelected(true);
        }
      }
    }
    useUIStore
      .getState()
      .showToast(`Auto-attack: ${scene.autoAttack ? "ON" : "OFF"}`);
  }

  // Auto-consume HP/MP potions during auto-attack when vitals fall below thresholds.
  function tryAutoPotion(time: number) {
    if (scene.dead) return;
    if (scene.autoPotInFlight) return;
    if (time < scene.nextAutoPotMs) return;
    const st = useCharacterStore.getState();
    if (st.maxHp <= 0 && st.maxMp <= 0) return;

    const ch = st.current;
    if (ch && inventory.charId !== ch.id) {
      inventory.setChar(ch.id);
      scene.inventoryRefreshAt = 0;
    }
    if (
      scene.inventoryRefreshAt === 0 ||
      time - scene.inventoryRefreshAt > 10000
    ) {
      scene.inventoryRefreshAt = time === 0 ? 1 : time;
      if (ch)
        inventory.refresh().catch(() => {
          /* swallow */
        });
    }

    const cfg = getSettingsSnapshot();
    if (!cfg.autoUseHpPotion && !cfg.autoUseMpPotion) return;
    const hpRatio = st.maxHp > 0 ? st.hp / st.maxHp : 1;
    const mpRatio = st.maxMp > 0 ? st.mp / st.maxMp : 1;
    let kind: "heal" | "mana" | null = null;
    if (cfg.autoUseHpPotion && hpRatio < cfg.hpPotionThreshold) kind = "heal";
    else if (cfg.autoUseMpPotion && mpRatio < cfg.mpPotionThreshold) kind = "mana";
    if (!kind) return;

    type Tpl = { id: number; type?: string; effect?: string };
    const tpls = itemsData.items as Tpl[];
    const shortcuts = usePotionShortcutStore.getState();
    const shortcutTplId =
      kind === "heal" ? shortcuts.hpTemplateId : shortcuts.mpTemplateId;

    const bag = inventory.items.filter(
      (x) => (x.slotType ?? "inventory") === "inventory",
    );

    let it = shortcutTplId
      ? bag.find((x) => x.templateId === shortcutTplId && x.quantity > 0)
      : undefined;
    if (!it) {
      const wantedIds = new Set(
        tpls
          .filter((t) => t.type === "consumable" && t.effect === kind)
          .map((t) => t.id),
      );
      if (wantedIds.size === 0) return;
      it = bag.find((x) => wantedIds.has(x.templateId) && x.quantity > 0);
    }
    if (!it) {
      if (ch)
        inventory.refresh().catch(() => {
          /* swallow */
        });
      scene.nextAutoPotMs = time + 1500;
      return;
    }

    const usedItemId = it.id;
    scene.autoPotInFlight = true;
    scene.nextAutoPotMs = time + 1500;
    ensureRealtime()
      .then((cli) =>
        cli.request<{
          ok: boolean;
          reason?: string;
          itemId?: number;
          templateId?: number;
          kind?: string;
          value?: number;
          hp?: number;
          mp?: number;
        }>(USEITEM, { itemId: usedItemId, kind }),
      )
      .then((resp) => {
        if (!resp || !resp.ok) {
          if (
            resp?.reason === "no_potion" ||
            resp?.reason === "not_consumable"
          ) {
            inventory.refresh().catch(() => {
              /* swallow */
            });
          }
          return;
        }
        const cs = useCharacterStore.getState();
        const patch: { hp?: number; mp?: number } = {};
        if (typeof resp.hp === "number") patch.hp = resp.hp;
        if (typeof resp.mp === "number") patch.mp = resp.mp;
        if (patch.hp !== undefined || patch.mp !== undefined)
          cs.setVitals(patch);
        scene.localPlayer?.setHp(patch.hp ?? cs.hp, cs.maxHp);
        const cached = inventory.items.find((x) => x.id === usedItemId);
        if (cached) {
          cached.quantity -= 1;
          if (cached.quantity <= 0)
            inventory.items = inventory.items.filter(
              (x) => x.id !== usedItemId,
            );
        }
        inventory.refresh().catch(() => {
          /* swallow */
        });
      })
      .catch(() => {
        /* swallow */
      })
      .finally(() => {
        scene.autoPotInFlight = false;
      });
  }

  // Click on a Zen pile: if in range, pickup immediately; otherwise set as walk-target.
  function clickPile(id: number) {
    const pile = scene.zenPiles.get(id);
    if (!pile || !pile.alive) return;
    if (!scene.localPlayer) return;
    const dx = pile.x - scene.localPlayer.x;
    const dy = pile.y - scene.localPlayer.y;
    const inRange =
      dx * dx + dy * dy <= scene.PICKUP_RANGE * scene.PICKUP_RANGE;
    if (inRange) {
      tryPickup(id);
    } else {
      const now = Date.now();
      if (
        pile.ownerUid &&
        pile.ownerUid !== scene.localUid &&
        (!pile.ownerUntil || now < pile.ownerUntil)
      ) {
        useUIStore.getState().showToast("Đồ rơi của người khác", "warn");
        return;
      }
      scene.pickupWalkTargetId = id;
    }
  }

  async function tryPickup(id: number) {
    if (scene.pickupsInFlight.has(id)) return;
    const pile = scene.zenPiles.get(id);
    if (!pile || !pile.alive) return;
    if (!scene.localPlayer) return;
    const dx = pile.x - scene.localPlayer.x;
    const dy = pile.y - scene.localPlayer.y;
    if (dx * dx + dy * dy > scene.PICKUP_RANGE * scene.PICKUP_RANGE) return;
    scene.pickupsInFlight.add(id);
    try {
      const cli = await ensureRealtime();
      const r = await cli.request<PickupResp>(PICKUP, { id });
      if (r.ok) {
        useCharacterStore.getState().setCurrency({ zen: r.newZen });
        useUIStore.getState().showToast(`+${r.amount} Zen`);
        const p = scene.zenPiles.get(id);
        if (p) {
          p.fadeOutAndDestroy();
          scene.zenPiles.delete(id);
        }
      } else if (r.reason && r.reason !== "too_far") {
        useUIStore.getState().showToast(`Pickup: ${r.reason}`, "warn");
      }
    } catch {
      // network hiccup; let the next tick retry
    } finally {
      scene.pickupsInFlight.delete(id);
    }
  }

  // Click on an EquipPile: if in range, request pickup; else set walk-target.
  function clickEquipPile(id: number) {
    const pile = scene.equipPiles.get(id);
    if (!pile || !pile.alive) return;
    if (!scene.localPlayer) return;
    const dx = pile.x - scene.localPlayer.x;
    const dy = pile.y - scene.localPlayer.y;
    const inRange =
      dx * dx + dy * dy <= scene.PICKUP_RANGE * scene.PICKUP_RANGE;
    if (inRange) {
      tryPickupEquip(id);
    } else {
      const now = Date.now();
      if (
        pile.ownerUid &&
        pile.ownerUid !== scene.localUid &&
        (!pile.ownerUntil || now < pile.ownerUntil)
      ) {
        useUIStore.getState().showToast("Đồ rơi của người khác", "warn");
        return;
      }
      scene.equipPickupWalkTargetId = id;
    }
  }

  async function tryPickupEquip(id: number) {
    if (scene.equipPickupsInFlight.has(id)) return;
    const pile = scene.equipPiles.get(id);
    if (!pile || !pile.alive) return;
    if (!scene.localPlayer) return;
    const dx = pile.x - scene.localPlayer.x;
    const dy = pile.y - scene.localPlayer.y;
    if (dx * dx + dy * dy > scene.PICKUP_RANGE * scene.PICKUP_RANGE) return;
    scene.equipPickupsInFlight.add(id);
    try {
      const cli = await ensureRealtime();
      const r = await cli.request<EquipPickupResp>(PICKUPEQUIP, { id });
      if (r.ok) {
        type Tpl = { id: number; name?: string };
        const tpls = itemsData.items as Tpl[];
        const tpl = tpls.find((t) => t.id === r.templateId);
        useUIStore
          .getState()
          .showToast(`Nhặt được: ${tpl?.name ?? `#${r.templateId}`}`);
        const p = scene.equipPiles.get(id);
        if (p) {
          p.fadeOutAndDestroy();
          scene.equipPiles.delete(id);
        }
        // Refresh inventory so the new item shows up in the bag.
        inventory.refresh().catch(() => {
          /* swallow */
        });
      } else if (r.reason && r.reason !== "too_far") {
        useUIStore.getState().showToast(`Pickup: ${r.reason}`, "warn");
      }
    } catch {
      // network hiccup; let the next tick retry
    } finally {
      scene.equipPickupsInFlight.delete(id);
    }
  }

  // While in auto-attack, walk toward the nearest pickable EquipPile if outside range.
  function autoWalkToNearestEquip(delta: number, time: number) {
    if (!scene.localPlayer || scene.equipPiles.size === 0) return;
    const cfg = getSettingsSnapshot();
    if (!cfg.autoWalkToLoot || !cfg.autoPickupEquip) return;
    const px = scene.localPlayer.x;
    const py = scene.localPlayer.y;
    const now = Date.now();
    const maxScan = 480;
    let best: EquipPile | undefined;
    let bestD = maxScan * maxScan;
    for (const pile of scene.equipPiles.values()) {
      if (!pile.alive) continue;
      if (
        pile.ownerUid &&
        pile.ownerUid !== scene.localUid &&
        (!pile.ownerUntil || now < pile.ownerUntil)
      )
        continue;
      const dx = pile.x - px;
      const dy = pile.y - py;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = pile;
      }
    }
    if (!best) return;
    if (bestD <= scene.PICKUP_RANGE * scene.PICKUP_RANGE) {
      tryPickupEquip(best.pileId);
      return;
    }

    const dx = best.x - px;
    const dy = best.y - py;
    const dist = Math.hypot(dx, dy) || 1;
    const speed = 0.2 * delta;
    let nx = Phaser.Math.Clamp(
      px + (dx / dist) * speed,
      16,
      FIELD_PIX_W - 16,
    );
    let ny = Phaser.Math.Clamp(
      py + (dy / dist) * speed,
      16,
      FIELD_PIX_H - 16,
    );
    const c = scene.collide(px, py, nx, ny);
    nx = c.x;
    ny = c.y;
    if (nx === px && ny === py) return;
    scene.localPlayer.x = nx;
    scene.localPlayer.y = ny;
    const dir =
      Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 2 : 0;
    useCharacterStore.getState().setPosition({ x: nx, y: ny });
    if (time - scene.lastSent > 100) {
      scene.lastSent = time;
      ensureRealtime()
        .then((cli) => cli.notify(MOVE, { x: nx, y: ny, d: dir }))
        .catch(() => {
          /* swallow */
        });
    }
  }

  return {
    findNearestMonster,
    autoWalkToNearestPile,
    autoWalkToNearestEquip,
    toggleAutoAttack,
    tryAutoPotion,
    clickPile,
    tryPickup,
    clickEquipPile,
    tryPickupEquip,
  };
}

export type FieldAutomation = ReturnType<typeof createFieldAutomation>;
